/**
 * FusionDocs (the group DMS) inside ONE. Everything here stays hidden unless /api/fusiondocs/status says it is
 * enabled. Knowledge shows FusionDocs search hits under ONE's own results; on a task each file can be filed to
 * FusionDocs, a filed file can be sent for signature, and the task's signature requests are listed.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileSignature, FileText, Library, Plus, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { api, ApiError, post } from "../lib/api";
import { Field, Modal } from "./ui";

/** True only when FusionDocs is switched on. Any error counts as off. */
export function useFusionDocs(): boolean {
  const q = useQuery({
    queryKey: ["fusiondocs-status"],
    queryFn: async () => {
      try { return (await api<{ enabled?: boolean }>("/api/fusiondocs/status")).enabled === true; } catch { return false; }
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
  return q.data === true;
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Something went wrong");
const day = (v?: string | null) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

// ── Knowledge ────────────────────────────────────────────────────────────────

export interface FusionDocsHit { id: string; title: string; type?: string; snippet?: string; url?: string; updatedAt?: string }
export interface FusionDocsResult { ok: boolean; hits: FusionDocsHit[] }

/** «match» → <mark>, like ONE's own snippets. */
function Snip({ text }: { text: string }) {
  const parts = text.replace(/\s+/g, " ").split(/(«[^»]*»)/g);
  return <>{parts.map((p, i) => (p.startsWith("«") ? <mark key={i} className="rounded bg-amber-100 px-0.5 text-inherit">{p.slice(1, -1)}</mark> : <span key={i}>{p}</span>))}</>;
}

export function FusionDocsHits({ result }: { result?: FusionDocsResult }) {
  if (!result) return null;
  if (!result.ok) return <p className="muted text-xs">FusionDocs didn't answer — showing ONE only</p>;
  return (
    <section className="space-y-2 pt-2">
      <h3 className="section-label flex items-center gap-1.5"><Library size={13} /> In FusionDocs</h3>
      {result.hits.length === 0 ? <p className="muted text-xs">Nothing in FusionDocs for this search.</p> : (
        <ul className="space-y-2">
          {result.hits.map((h) => {
            const body = (
              <>
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <FileText size={14} className="muted shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{h.title || "Untitled"}</span>
                  {h.type && <span className="chip shrink-0 !text-[0.7rem]">{h.type}</span>}
                  {h.url && <ExternalLink size={13} className="muted shrink-0" />}
                </span>
                {h.snippet && <span className="muted mt-1 line-clamp-2 block text-xs"><Snip text={h.snippet} /></span>}
                <span className="muted mt-1.5 block truncate text-[0.7rem]">{["FusionDocs", day(h.updatedAt)].filter(Boolean).join(" · ")}</span>
              </>
            );
            const cls = "block rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3";
            return (
              <li key={h.id}>
                {h.url ? <a href={h.url} target="_blank" rel="noreferrer" className={`${cls} hover:border-brand-300`}>{body}</a> : <div className={cls}>{body}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ── Task files ───────────────────────────────────────────────────────────────

export interface FusionDocsLink { documentId: string; fusionDocsId: string | null; url: string | null; status?: string; filedAt: string }
export interface FusionDocsSignature { id: string; fusionDocsId: string; status: string; signers: Array<{ name?: string; email: string }>; createdAt: string; updatedAt: string }
interface TaskFusionDocs { enabled: boolean; signaturesEnabled?: boolean; links: FusionDocsLink[]; signatures: FusionDocsSignature[] }

const taskKey = (itemId: string) => ["item-fusiondocs", itemId];

export function useTaskFusionDocs(itemId: string, enabled: boolean) {
  // while FusionDocs is still scanning a file, look again every few seconds
  return useQuery({ queryKey: taskKey(itemId), queryFn: () => api<TaskFusionDocs>(`/api/work/items/${itemId}/fusiondocs`), enabled,
    refetchInterval: (q) => (q.state.data?.links.some((l) => l.status === "processing") ? 4000 : false) });
}

/** Per-file control: "In FusionDocs" chip + "Get it signed" when filed, else "File to FusionDocs". */
export function FileFusionDocs({ itemId, documentId, filename, link, canFile, compact, canSign = false }: {
  itemId: string; documentId: string; filename: string; link?: FusionDocsLink; canFile: boolean; compact?: boolean; canSign?: boolean;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);

  const file = async () => {
    setBusy(true); setError(null);
    try {
      await post<{ ok: boolean; fusionDocsId: string; url: string | null; already?: boolean }>(`/api/work/items/${itemId}/attachments/${documentId}/fusiondocs`, {});
    } catch (e) { setError(errText(e)); }
    setBusy(false);
    void qc.invalidateQueries({ queryKey: taskKey(itemId) });
  };

  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
      {link?.status === "processing" ? (
        <span className="chip !text-[0.7rem]"><Library size={11} /> Filing… (virus scan)</span>
      ) : link && link.status !== "rejected" && link.status !== "quarantined" && link.status !== "scan_failed" ? (
        <>
          {link.url
            ? <a href={link.url} target="_blank" rel="noreferrer" className="chip !text-[0.7rem] hover:border-brand-300" title="Open in FusionDocs"><Library size={11} /> In FusionDocs</a>
            : <span className="chip !text-[0.7rem]"><Library size={11} /> In FusionDocs</span>}
          {canFile && canSign && <button className="btn btn-ghost btn-sm !px-1.5 text-xs" onClick={() => setSigning(true)}><FileSignature size={13} /> {compact ? "Sign" : "Get it signed"}</button>}
        </>
      ) : canFile && (
        <button className="btn btn-ghost btn-sm !px-1.5 text-xs" disabled={busy} onClick={() => void file()}><Library size={13} /> {busy ? "Filing…" : compact ? "File" : "File to FusionDocs"}</button>
      )}
      {error && <span className="text-xs text-red-700">{error}</span>}
      {signing && link?.fusionDocsId && <SignatureForm itemId={itemId} fusionDocsId={link.fusionDocsId} filename={filename} onClose={() => setSigning(false)} />}
    </span>
  );
}

function SignatureForm({ itemId, fusionDocsId, filename, onClose }: { itemId: string; fusionDocsId: string; filename: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [signers, setSigners] = useState<Array<{ name: string; email: string }>>([{ name: "", email: "" }]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = signers.length > 0 && signers.every((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim()));
  const set = (i: number, k: "name" | "email", v: string) => setSigners((l) => l.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const send = async () => {
    setBusy(true); setError(null);
    try {
      await post(`/api/work/items/${itemId}/fusiondocs/signature`, {
        fusionDocsId,
        signers: signers.map((s) => ({ ...(s.name.trim() ? { name: s.name.trim() } : {}), email: s.email.trim() })),
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      void qc.invalidateQueries({ queryKey: taskKey(itemId) });
      onClose();
    } catch (e) { setError(errText(e)); setBusy(false); }
  };

  return (
    <Modal title="Get it signed" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!valid || busy} onClick={() => void send()}><Send size={14} /> {busy ? "Sending…" : "Send for signature"}</button></>}>
      <p className="muted truncate text-sm">{filename}</p>
      <div className="space-y-2">
        <span className="block text-[0.8125rem] font-semibold text-[var(--text-2)]">Signers</span>
        {signers.map((s, i) => (
          <div key={i} className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input className="input min-w-0 sm:flex-1" placeholder="Name (optional)" value={s.name} onChange={(e) => set(i, "name", e.target.value)} aria-label={`Signer ${i + 1} name`} />
            <div className="flex min-w-0 gap-2 sm:flex-[1.3]">
              <input className="input min-w-0 flex-1" type="email" placeholder="Email" value={s.email} onChange={(e) => set(i, "email", e.target.value)} aria-label={`Signer ${i + 1} email`} />
              <button className="btn btn-ghost btn-sm !px-1.5" disabled={signers.length <= 1} onClick={() => setSigners((l) => l.filter((_, j) => j !== i))} aria-label={`Remove signer ${i + 1}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {signers.length < 10 && <button className="btn btn-ghost btn-sm" onClick={() => setSigners((l) => [...l, { name: "", email: "" }])}><Plus size={14} /> Add signer</button>}
      </div>
      <Field label="Message (optional)">
        <textarea className="input min-h-[5rem] w-full" maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
      </Field>
      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800">{error}</div>}
    </Modal>
  );
}

const STATUS_TONE: Record<string, string> = {
  signed: "!border-emerald-200 !bg-emerald-50 !text-emerald-800", completed: "!border-emerald-200 !bg-emerald-50 !text-emerald-800",
  declined: "!border-red-200 !bg-red-50 !text-red-800", rejected: "!border-red-200 !bg-red-50 !text-red-800", cancelled: "!border-red-200 !bg-red-50 !text-red-800", expired: "!border-red-200 !bg-red-50 !text-red-800",
};

export function FusionDocsSignatures({ signatures, names }: { signatures: FusionDocsSignature[]; names: Map<string, string> }) {
  if (!signatures.length) return null;
  return (
    <div className="mt-4 border-t border-[var(--line)] pt-3">
      <h3 className="section-label mb-1.5 flex items-center gap-1.5"><FileSignature size={13} /> Signatures</h3>
      <ul className="divide-y divide-[var(--line)]">
        {signatures.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <span className={`chip !text-[0.7rem] capitalize ${STATUS_TONE[s.status.toLowerCase()] ?? ""}`}>{s.status.replace(/_/g, " ")}</span>
            <span className="min-w-0 flex-1 break-words">
              {names.get(s.fusionDocsId) && <span className="font-medium">{names.get(s.fusionDocsId)} · </span>}
              {s.signers.map((x) => x.name || x.email).join(", ")}
            </span>
            <span className="muted shrink-0 text-xs">{day(s.updatedAt || s.createdAt)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
